/**
 * Shrink an ID-card photo in the browser before uploading it.
 *
 * Phone cameras produce 4–12 MB JPEGs. The API caps uploads at 5 MB and the hosting proxy
 * has its own body limit, so a straight-from-the-camera photo could be rejected — or worse,
 * truncated in transit, which is what made a correctly-filled booking form come back with
 * "please provide an ID card". Downscaling to ~1600px and re-encoding gets a legible ID
 * card down to a few hundred kilobytes.
 *
 * Any failure returns the original file: a slightly-too-large upload that the server can
 * reject with a clear message beats losing the guest's photo entirely.
 */

const MAX_DIMENSION = 1600;
const TARGET_BYTES = 900 * 1024;
const QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5];

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the image'));
    };
    img.src = url;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));

export async function compressImage(file) {
  if (!file || !file.type?.startsWith('image/')) return file;
  // Already small enough — re-encoding would only lose detail.
  if (file.size <= TARGET_BYTES) return file;

  try {
    const img = await loadImage(file);

    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);

    const ctx = canvas.getContext('2d');
    // White ground so a transparent PNG does not turn black once flattened to JPEG.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    let blob = null;
    for (const quality of QUALITY_STEPS) {
      blob = await canvasToBlob(canvas, quality);
      if (blob && blob.size <= TARGET_BYTES) break;
    }
    if (!blob || blob.size >= file.size) return file;

    // Always name the result `.jpg`: the server allowlists by extension, and phones hand
    // back `.heic`/`.jfif` names that would otherwise be refused.
    return new File([blob], `id-card-${Date.now()}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  } catch (err) {
    console.warn('Image compression skipped:', err.message);
    return file;
  }
}

export const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
